import React, { useCallback, useEffect, useState, useContext } from "react";
import { NotificationManager } from "react-notifications";
import { Link, NavLink } from "react-router-dom";
import useApi from "../Api/Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import Button from "../Components/Button";
import { FilterContext } from "../FilterContextProvider";
import useLocalState from "../hooks/useLocalState";
import storageManager from "../Utility/StorageManager";
import ErrorBoundary from "../Components/ErrorBoundary";
import OnlineWrapper from "./OnlineWrapper";

const EventLayout = ({ children, dataCached, setDataCached, afterSubmit }) => {
  const [isFetching, setIsFetching] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const { filterData } = useContext(FilterContext);
  const [allSales, setAllSales] = useState();
  const [products, setProducts] = useState([]);
  const api = useApi();
  const chosen = "py-2 px-3 mr-2 rounded-md text-blue-600 bg-blue-50";
  const notChosen = "py-2 px-3 mr-2 hover:text-blue-500";

  const [event, setEvent] = useLocalState(`event/${filterData.event_id}/info`, {
    date: "",
  });

  const cachedSales = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/sales`));

  const cachedFees = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/fees`));

  const cachedClothingSetup = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/clothingSetup`));

  const cachedOtherSetup = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/otherSetup`));

  const cachedMusicSetup = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/musicSetup`));

  const cachedTempTaxPercent = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/tempTaxPercent`));

  const cachedTempCCPercent = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/tempCCPercent`));

  let eventFeesToAdd = [];
  let eventFeesToRemove = [];
  if (cachedFees) {
    eventFeesToAdd = cachedFees.filter((each) => each.toAdd === true);
    eventFeesToRemove = cachedFees.filter((each) => each.toRemove === true);
  }

  let salesToUpdate = [];
  if (cachedSales) {
    salesToUpdate = Object.values(cachedSales);
  }

  let cachedHallRates = [];

  cachedHallRates.push(cachedClothingSetup, cachedOtherSetup, cachedMusicSetup);

  const getEvent = useCallback(() => {
    api.get(`/events/${filterData.event_id}`).then((r) => {
      setEvent(r.data);
    });
  }, [api, setEvent, filterData.event_id]);

  const getProducts = useCallback(() => {
    api.get(`/products/all?artist_id=${filterData.artist_id}&active=true`).then((r) => {
      setProducts(r.data);
    });
  }, [api, setProducts, filterData.artist_id]);

  const getAllSales = useCallback(() => {
    api.get(`/sales/all?artist_id=${filterData.artist_id}&event_id=${filterData.event_id}`).then((r) => {
      setAllSales(r.data);
    });
  }, [filterData, api]);

  const handleTempData = (e) => {
    const eventToUpdate = JSON.parse(storageManager.getItem(`event/${filterData.event_id}/info`));
    eventToUpdate.tax_rate = cachedTempTaxPercent / 100;
    eventToUpdate.cc_rate = cachedTempCCPercent / 100;
    e.preventDefault();
    salesToUpdate.forEach((one) => {
      one.sale_id ? (one.id = one.sale_id) : delete one.id;
    });
    event.tax_rate = cachedTempTaxPercent / 100;
    event.cc_rate = cachedTempCCPercent / 100;
    api
      .put(`/events/${event.id}`, {
        event: {
          ...eventToUpdate,
          event_category_setups_attributes: cachedHallRates.map((each) => ({
            id: each.id,
            name: each.name,
            hall_rate: each.hall_rate,
            taxed: each.taxed,
            gross_sales: each.gross_sales,
            gross_tax: each.gross_tax,
            gross_hall: each.gross_hall,
            category_id: each.category_id,
            event_id: event.id,
          })),
          event_fees_attributes: [
            ...eventFeesToAdd.map((each) => ({
              id: each.id,
              amount: each.amount,
              description: each.description,
              before_hall_fee: each.before_hall_fee,
              event_id: each,
            })),
            ...eventFeesToRemove.map((each) => ({
              id: each.id,
              _destroy: "1",
            })),
          ],
          sales_attributes: salesToUpdate.map((each) => each),
        },
      })
      .then(() => {
        NotificationManager.success(`Event successfully updated`, "Success", 2000);
        getEvent();
        setDataCached(false);
        for (let key of storageManager.keys()) {
          if (key === `event/${event.id}/sales`) {
            storageManager.removeItem(key);
          }
        }
        afterSubmit();
        getAllSales();
        setDataCached(false);
      })
      .catch((error) => {
        setErrorMessage(error.response.data);
      })
      .finally(() => {
        storageManager.keys().find((key) => key.startsWith(`event/${event.id}`)) === undefined && setDataCached(false);
      });
    setIsFetching(false);
  };

  useEffect(() => {
    !storageManager.getItem(`event/${filterData.event_id}/info`) && getAllSales();
  }, [getAllSales, filterData.event_id]);

  useEffect(() => {
    !storageManager.getItem(`event/${filterData.event_id}/info`) && getProducts();
  }, [getProducts, filterData.event_id]);

  useEffect(() => {
    !storageManager.getItem(`event/${filterData.event_id}/info`) && getEvent();
  }, [getEvent, filterData.event_id]);

  const [year, month, day] = event.date.split("T")[0].split("-");
  event.formattedDate = `${month}/${day}/${year}`;

  if (allSales) {
    if (allSales.length === 0) {
      products
        .filter((prod) => prod.active)
        .forEach((each) => {
          each.new = true;
          each.product_id = each.id;
          each.product_name = each.name;
          each.event_id = event.id;
          each.product_category = each.category?.name;
          each.category_id = each.category?.id;
          each.size_id = each.size?.id;
          each.price = each.retail_price;
          if (each.size_other) {
            each.product_size = each.size_other;
          } else if (each.size?.name) {
            each.product_size = each.size.name;
          } else if (!each.size) {
            each.product_size = null;
          }
          each.discount_price = 0;
          each.count_in = 0;
          each.add = 0;
          each.add_2 = 0;
          each.add_3 = 0;
          each.add_4 = 0;
          each.count_out = 0;
          each.discount_quantity = 0;
          each.band_comp_quantity = 0;
          each.ctm_comp = 0;
          each.management_comp = 0;
          each.damage_quantity = 0;
          each.total = 0;
          each.discount_total = 0;
          each.reporting_type = "show";
          each.date = event.date;
          let currentEventState = JSON.parse(storageManager.getItem(`event/${event.id}/sales`) ?? "{}");
          currentEventState[each.id] = each;
          storageManager.setItem(`event/${event.id}/sales`, JSON.stringify(currentEventState));
        });
    }
  }

  return (
    <div className="text-black bg-gray-100 pt-2 pb-1">
      <div className="mt-8 mx-8 pt-4 px-12 text-black bg-white rounded-lg border">
        <div className="mt-1 font-medium -mx-2 text-black text-sm">
          <Link to="/sales/events">
            <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to events list
          </Link>
        </div>
        <div className="py-2 -mx-2 text-2xl font-medium">
          {event.name} - {event.formattedDate}
        </div>
        <div className="flex flex-row -mx-2 mt-4 mb-6 text-sm text-black w-full">
          <NavLink to={`/sales/events/${filterData.event_id}/details/`} activeClassName={chosen} className={notChosen}>
            <p>Event Details</p>
          </NavLink>
          <NavLink to={`/sales/events/${filterData.event_id}/tonight/`} activeClassName={chosen} className={notChosen}>
            <p>Tonight's Products</p>
          </NavLink>
          <NavLink to={`/sales/events/${filterData.event_id}/sales/`} activeClassName={chosen} className={notChosen}>
            <p>Event Sales</p>
          </NavLink>
          <NavLink to={`/sales/events/${filterData.event_id}/summary/`} activeClassName={chosen} className={notChosen}>
            <p>Event Summary</p>
          </NavLink>
        </div>
        <ErrorBoundary>
          <div className="sales-content">{children}</div>
          {dataCached && (
            <OnlineWrapper>
              <div className="flex flex-row mt-12 justify-center">
                <div className="text-green-500 text-sm mr-4 self-center">
                  You appear to be online, click sync to upload changes that were made while you were offline.
                </div>
                <Button
                  className="button"
                  onClick={handleTempData}
                  text={isFetching ? `Loading ${"\u00A0"}` : "Sync"}
                  disabled={isFetching}
                  inProgress={isFetching}
                ></Button>
              </div>
            </OnlineWrapper>
          )}
        </ErrorBoundary>
        <div className="flex justify-center mt-8">
          {errorMessage?.error && <div className="text-red-500 pb-2">{errorMessage.error}</div>}
        </div>
      </div>
    </div>
  );
};

export default EventLayout;
